'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { BlurFade } from '@/components/ui/blur-fade'
import ImageLoading from '@/components/ui/image-loading'
import { Button } from '@/components/ui/button'
import { trackMeaningfulOutcomeCompleted } from '@/lib/analytics/meaningful-outcome'
import {
  IconDownload,
  IconCube,
  IconPlayerPlay,
  IconPlayerPause,
  IconRefresh
} from '@tabler/icons-react'
import dynamic from 'next/dynamic'
import { cn } from '@/lib/utils'
import { logger } from '@/lib/logger'
import type { ThreeDModelViewerFormat } from '@/components/visual-artifacts/three-d-model-viewer'

// three.js (~210 KB gz) loads only when a 3D model is actually rendered
const ThreeDModelViewer = dynamic(
  () =>
    import('@/components/visual-artifacts/three-d-model-viewer').then(
      (module) => module.ThreeDModelViewer
    ),
  { loading: () => null }
)

// Lightweight model viewer wrapper using the <model-viewer> web component
// Renders GLB/GLTF inline. For unsupported formats (e.g., PLY), shows a rich file card with download.

interface AnimatedMarkdown3DProps {
  src: string
  alt?: string
  isStreaming: boolean
  chatId?: string
  messageId?: string
  shouldTrackOutcome?: boolean
}

const SupportedInline = ['glb', 'gltf', 'obj', 'ply', 'stl'] as const

function isThreeDModelViewerFormat(value: string): value is ThreeDModelViewerFormat {
  return SupportedInline.some((format) => format === value)
}

function getExt(url: string): string {
  try {
    const u = new URL(url)
    const pathname = u.pathname
    const ext = pathname.split('.').pop() || ''
    return ext.toLowerCase()
  } catch {
    const parts = url.split('?')[0].split('#')[0].split('.')
    return (parts.pop() || '').toLowerCase()
  }
}

export const AnimatedMarkdown3D: React.FC<AnimatedMarkdown3DProps> = ({
  src,
  alt = '3D Model',
  isStreaming,
  chatId,
  messageId,
  shouldTrackOutcome = false
}) => {
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [show, setShow] = useState(false)
  const [autoRotate, setAutoRotate] = useState(true)
  const viewerRef = useRef<any>(null)

  const ext = useMemo(() => getExt(src), [src])
  const inline = SupportedInline.includes(ext as (typeof SupportedInline)[number])

  useEffect(() => {
    if (!isStreaming && src) {
      const timer = setTimeout(() => setShow(true), 200)
      return () => clearTimeout(timer)
    }
  }, [isStreaming, src])

  // Load model-viewer script on client when needed
  useEffect(() => {
    if (!inline) return
    setReady(true)
  }, [inline])

  const handleDownload = () => {
    try {
      const a = document.createElement('a')
      a.href = src
      a.download = src.split('/').pop() || `model.${ext || 'glb'}`
      a.rel = 'noopener noreferrer'
      a.target = '_blank'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      if (shouldTrackOutcome) {
        void trackMeaningfulOutcomeCompleted({
          surface: 'chat',
          outcome_type: 'saved_output',
          qualifying_action: 'exported',
          chat_id: chatId,
          message_id: messageId,
          source: '3d_model'
        })
      }
    } catch (e) {
      console.error(e)
      setError('Download failed')
    }
  }

  if (isThreeDModelViewerFormat(ext)) {
    return (
      <div className="relative my-4 w-full">
        <BlurFade duration={0.5} delay={0.1} className="w-full">
          <ThreeDModelViewer
            assetUrl={src}
            downloadFileName={src.split('/').pop() || `model.${ext}`}
            format={ext}
            title={alt || '3D asset'}
          />
        </BlurFade>
      </div>
    )
  }

  if (error) {
    return (
      <div className="my-3 rounded-md border p-3 text-sm text-red-600 dark:text-red-400">
        {error}
      </div>
    )
  }

  // Interactive PLY fallback via Three.js (dynamically imported)
  if (!inline && ext === 'ply') {
    const PlyViewer: React.FC<{ src: string; alt?: string }> = ({ src, alt }) => {
      const mountRef = useRef<HTMLDivElement | null>(null)
      const [plyError, setPlyError] = useState<string | null>(null)

      useEffect(() => {
        let disposed = false
        let renderer: any, scene: any, camera: any, controls: any, mesh: any, rafId: number

        async function init() {
          try {
            const THREE = await import('three')
            // @ts-ignore examples modules
            const { OrbitControls } = await import('three/examples/jsm/controls/OrbitControls.js')
            // @ts-ignore examples modules
            const { PLYLoader } = await import('three/examples/jsm/loaders/PLYLoader.js')

            if (disposed) return
            const width = mountRef.current?.clientWidth || 800
            const height = mountRef.current?.clientHeight || 450

            renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
            renderer.setSize(width, height)
            renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
            mountRef.current?.appendChild(renderer.domElement)

            scene = new THREE.Scene()
            scene.background = null

            camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000)
            camera.position.set(0, 0.15, 1.2)

            const hemi = new THREE.HemisphereLight(0xffffff, 0x444444, 1.0)
            scene.add(hemi)
            const dir = new THREE.DirectionalLight(0xffffff, 0.8)
            dir.position.set(1, 1, 1)
            scene.add(dir)

            controls = new OrbitControls(camera, renderer.domElement)
            controls.enableDamping = true
            controls.dampingFactor = 0.05
            controls.enablePan = true
            controls.enableZoom = true

            const loader = new PLYLoader()
            loader.load(
              src,
              (geometry: any) => {
                geometry.computeVertexNormals?.()
                geometry.center?.()
                const material = new THREE.MeshStandardMaterial({
                  color: 0xcccccc,
                  metalness: 0.1,
                  roughness: 0.8
                })
                mesh = new THREE.Mesh(geometry, material)
                mesh.rotation.x = -Math.PI / 2
                scene.add(mesh)
              },
              undefined,
              (e: any) => {
                console.error(e)
                setPlyError('Failed to load PLY model')
              }
            )

            const onResize = () => {
              if (!mountRef.current) return
              const w = mountRef.current.clientWidth
              const h = mountRef.current.clientHeight
              renderer.setSize(w, h)
              camera.aspect = w / h
              camera.updateProjectionMatrix()
            }
            window.addEventListener('resize', onResize)

            const tick = () => {
              if (disposed) return
              controls.update()
              renderer.render(scene, camera)
              rafId = requestAnimationFrame(tick)
            }
            tick()

            return () => {
              window.removeEventListener('resize', onResize)
            }
          } catch (err) {
            console.error(err)
            setPlyError('Three.js initialization failed')
          }
        }

        const cleanup = init()

        return () => {
          disposed = true
          if (rafId) cancelAnimationFrame(rafId)
          try {
            // @ts-ignore
            if (mesh?.geometry) mesh.geometry.dispose()
            // @ts-ignore
            if (mesh?.material) mesh.material.dispose()
            renderer?.dispose?.()
            const el = mountRef.current?.firstChild
            if (el && el.parentNode) el.parentNode.removeChild(el)
          } catch {}
        }
      }, [src])

      if (plyError) {
        return (
          <div className="my-3 rounded-md border p-3 text-sm text-red-600 dark:text-red-400">
            {plyError}
          </div>
        )
      }

      return (
        <div className="bg-muted/30 relative h-[360px] w-full overflow-hidden rounded-lg border">
          <div ref={mountRef} className="size-full" aria-label={alt || 'PLY Viewer'} />
        </div>
      )
    }

    return (
      <div className="relative my-4 w-full">
        <BlurFade duration={0.5} delay={0.1} className="w-full">
          <PlyViewer src={src} alt={alt} />
        </BlurFade>
      </div>
    )
  }

  return (
    <div className="relative my-4 w-full">
      <BlurFade duration={0.5} delay={0.1} className="w-full">
        <div
          className={cn(
            'bg-muted/30 relative w-full overflow-hidden rounded-lg border',
            inline ? 'aspect-video' : 'p-4'
          )}
        >
          {!ready && inline && (
            <div className="absolute inset-0 z-10 flex items-center justify-center">
              <ImageLoading />
            </div>
          )}

          {inline ? (
            show && (
              <>
                {/* @ts-ignore - model-viewer is a custom element */}
                <model-viewer
                  ref={viewerRef}
                  src={src}
                  alt={alt}
                  camera-controls
                  enable-pan
                  auto-rotate={autoRotate}
                  rotation-per-second="30deg"
                  camera-orbit="0deg 75deg 1.5m"
                  field-of-view="30deg"
                  min-camera-orbit="auto auto 0.5m"
                  max-camera-orbit="auto auto 3m"
                  interaction-prompt="auto"
                  interaction-prompt-threshold="2000"
                  exposure="1.0"
                  shadow-intensity="0.8"
                  shadow-softness="1.0"
                  environment-image="neutral"
                  style={{
                    width: '100%',
                    height: '100%',
                    background: 'transparent',
                    outline: 'none',
                    touchAction: 'manipulation'
                  }}
                  onLoad={() => {
                    setReady(true)
                    logger.debug('3D model loaded successfully')
                  }}
                  onError={(e: any) => {
                    logger.error('3D model load error:', e)
                    setError('Failed to render 3D model')
                  }}
                />

                {/* Controls overlay - only show when model is loaded */}
                {ready && (
                  <div className="pointer-events-auto absolute right-2 top-2 z-20 flex gap-2">
                    <Button
                      size="icon"
                      variant="secondary"
                      className="size-8"
                      onClick={() => setAutoRotate((r) => !r)}
                    >
                      {autoRotate ? <IconPlayerPause size={16} /> : <IconPlayerPlay size={16} />}
                    </Button>
                    <Button
                      size="icon"
                      variant="secondary"
                      className="size-8"
                      onClick={() => {
                        try {
                          const el: any = viewerRef.current
                          if (el) {
                            el.cameraOrbit = '0deg 75deg 1.5m'
                            el.cameraTarget = '0m 0m 0m'
                          }
                        } catch (e) {
                          console.error('Reset camera error:', e)
                        }
                      }}
                      title="Reset Camera"
                    >
                      <IconRefresh size={16} />
                    </Button>
                  </div>
                )}
              </>
            )
          ) : (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="bg-background rounded-md p-2 shadow-sm">
                  <IconCube size={20} />
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{alt || '3D Asset'}</div>
                  <div className="text-muted-foreground truncate text-xs">{src}</div>
                </div>
              </div>
              <Button size="sm" variant="secondary" onClick={handleDownload}>
                <IconDownload size={16} className="mr-1" /> Download
              </Button>
            </div>
          )}
        </div>
      </BlurFade>
    </div>
  )
}
